/**
 * Historical Trends Chart Component
 *
 * World-class pharmaceutical analytics trend chart using ApexCharts.
 * Displays maturity score progression over time with:
 * - Score band annotations (Excellent, Good, Acceptable, Fair, Poor, Critical)
 * - Interactive tooltips
 * - Zoom and pan controls
 * - Trend indicator (improving, declining, stable)
 *
 * Design: Clinical trial-grade precision, audit-ready presentation
 * Spec: Documentation/Development/Design Docs/Maturity_Dashboard_UI_UX_Specification.md
 */

import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

// ============================================================================
// TYPES
// ============================================================================

interface HistoricalDataPoint {
  timestamp: string;       // ISO date
  score: number;           // 0-100
  classification?: 'excellent' | 'good' | 'acceptable' | 'fair' | 'poor' | 'critical';
  delta?: number;          // Change from previous
  stationsCompleted?: number;
}

interface HistoricalTrendsChartProps {
  data: HistoricalDataPoint[];
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate trend indicator from historical data
 */
function getTrendIndicator(data: HistoricalDataPoint[]): string {
  if (data.length < 2) return '';

  const first = data[0].score;
  const last = data[data.length - 1].score;
  const change = last - first;

  if (Math.abs(change) < 2) return '→ Stable Trend';
  if (change > 0) return '↗ Improving Trend';
  return '↘ Declining Trend';
}

// ============================================================================
// COMPONENT
// ============================================================================

export const HistoricalTrendsChart: React.FC<HistoricalTrendsChartProps> = ({ data }) => {

  // Normalize data - ensure scores are numbers (API may return strings)
  const normalizedData = data.map(d => ({
    ...d,
    score: typeof d.score === 'number' ? d.score : parseFloat(d.score as unknown as string),
  })).filter(d => !isNaN(d.score)); // Remove invalid entries

  // Transform data for ApexCharts
  const series = [{
    name: 'Maturity Score',
    data: normalizedData.map(d => ({
      x: new Date(d.timestamp).getTime(),
      y: d.score
    }))
  }];

  const options: ApexOptions = {
    chart: {
      type: 'line',
      height: 400,
      background: 'transparent',
      foreColor: '#9da5b8',
      toolbar: {
        show: true,
        tools: {
          download: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: false,
          reset: true
        }
      },
      zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: false
      }
    },
    theme: {
      mode: 'dark'
    },
    stroke: {
      curve: 'smooth',
      width: 3,
      colors: ['#4FC3F7']
    },
    markers: {
      size: 6,
      colors: ['#4FC3F7'],
      strokeColors: '#1a1d29',
      strokeWidth: 2,
      hover: {
        size: 8
      }
    },
    xaxis: {
      type: 'datetime',
      labels: {
        style: {
          colors: '#9da5b8',
          fontSize: '12px'
        },
        datetimeFormatter: {
          year: 'yyyy',
          month: 'MMM yyyy',
          day: 'MMM dd',
          hour: 'HH:mm'
        }
      },
      axisBorder: {
        color: '#3d4152'
      },
      axisTicks: {
        color: '#3d4152'
      }
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 5,
      labels: {
        style: {
          colors: '#9da5b8',
          fontSize: '12px'
        },
        formatter: (value) => `${value}`
      },
      title: {
        text: 'Maturity Score',
        style: {
          color: '#9da5b8',
          fontSize: '14px',
          fontWeight: 600
        }
      }
    },
    grid: {
      borderColor: '#3d4152',
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    annotations: {
      yaxis: [
        {
          y: 90,
          borderColor: '#27AE60',
          strokeDashArray: 0,
          opacity: 0.2,
          label: {
            text: 'Excellent',
            position: 'right',
            style: {
              color: '#27AE60',
              background: 'transparent'
            }
          }
        },
        {
          y: 75,
          borderColor: '#3498DB',
          strokeDashArray: 4,
          opacity: 0.3,
          label: {
            text: 'Good',
            position: 'right',
            style: {
              color: '#3498DB',
              background: 'transparent'
            }
          }
        },
        {
          y: 60,
          borderColor: '#F39C12',
          strokeDashArray: 4,
          opacity: 0.3,
          label: {
            text: 'Acceptable',
            position: 'right',
            style: {
              color: '#F39C12',
              background: 'transparent'
            }
          }
        },
        {
          y: 40,
          borderColor: '#E67E22',
          strokeDashArray: 4,
          opacity: 0.3,
          label: {
            text: 'Fair',
            position: 'right',
            style: {
              color: '#E67E22',
              background: 'transparent'
            }
          }
        },
        {
          y: 20,
          borderColor: '#E74C3C',
          strokeDashArray: 4,
          opacity: 0.3,
          label: {
            text: 'Poor',
            position: 'right',
            style: {
              color: '#E74C3C',
              background: 'transparent'
            }
          }
        }
      ]
    },
    tooltip: {
      theme: 'dark',
      x: {
        format: 'MMM dd, yyyy HH:mm'
      },
      y: {
        formatter: (value) => `${value}/100`,
        title: {
          formatter: () => 'Score:'
        }
      },
      marker: {
        show: true
      }
    },
    legend: {
      show: false
    }
  };

  const trendIndicator = getTrendIndicator(normalizedData);

  if (!normalizedData || normalizedData.length === 0) {
    return (
      <div className="historical-trends-chart">
        <div className="section-header">
          <h2>Historical Trends</h2>
        </div>
        <div className="chart-empty-state">
          <div className="empty-icon">📈</div>
          <div className="empty-message">No historical data available yet.</div>
          <div className="empty-hint">
            Complete multiple assessments to see trends over time.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="historical-trends-chart">
      <div className="section-header">
        <div>
          <h2>Historical Trends</h2>
          <div className="trend-metadata">
            <span>{normalizedData.length} assessment{normalizedData.length !== 1 ? 's' : ''} over time</span>
            {trendIndicator && (
              <span className="trend-indicator">
                {trendIndicator}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="chart-container">
        <ReactApexChart
          options={options}
          series={series}
          type="line"
          height={400}
        />
      </div>
    </div>
  );
};
